import type { AnalysisPreset } from "./analysis-presets";
import { resolveThreads, getEngineUrl, supportsThreadedAnalysis } from "./analysis-presets";
import type { DesktopStockfish, EngineTransport } from "./native-stockfish";
import { createNativeTransport, desktopStockfish, nativeSearchOptions } from "./native-stockfish";

export interface PositionEval {
  /** Centipawns from mover's perspective */
  cp: number;
  /** Mate in N (positive = mover wins, negative = mover loses). Null if no mate found. */
  mate: number | null;
  /** Best move in UCI format (e.g. "e2e4") */
  bestMove: string;
  /** Principal variation (array of UCI moves) */
  pv: string[];
  /** Depth reached */
  depth: number;
  /** Nodes searched */
  nodes: number;
  /** Additional PV lines (for multiPv > 1) */
  pvLines: PvLine[];
}

export interface StockfishSearchProgress {
  depth: number;
  nodes: number;
  targetDepth: number;
  /** Provisional exact score, never an aspiration-search bound. */
  evaluation?: PositionEval;
}

export interface PvLine {
  rank: number;
  depth: number;
  cp: number;
  mate: number | null;
  moveUci: string;
  continuation: string[];
  wdl?: { win: number; draw: number; loss: number };
}

const MIN_FULL_ENGINE_WASM_BYTES = 80 * 1024 * 1024;
const MIN_STOCKFISH_ELO = 1320;
const MAX_STOCKFISH_ELO = 3190;

export const clampStockfishElo = (rating: number): number =>
  Math.min(MAX_STOCKFISH_ELO, Math.max(MIN_STOCKFISH_ELO, Math.round(rating)));

class FullEngineAssetMismatchError extends Error {
  constructor(receivedBytes: number) {
    super(
      `Deep analysis requires the 108 MB engine, but the server returned a ${(receivedBytes / 1024 / 1024).toFixed(1)} MB WASM file. Refresh the app and try again.`,
    );
    this.name = "FullEngineAssetMismatchError";
  }
}

/**
 * Wrapper around a Stockfish Web Worker, or native Stockfish on the desktop app. Handles UCI communication,
 * initialization, and position evaluation.
 */
export class StockfishWorker {
  private worker: EngineTransport | null = null;
  private engineUrl: string | null = null;
  /** Only the deepest info line per PV is needed to build the final result.
   * Keeping every line made deep searches repeatedly parse an ever-growing
   * buffer on the browser main thread. */
  private latestInfoByPv = new Map<number, string>();
  private resolveReady: (() => void) | null = null;
  private rejectReady: ((reason?: unknown) => void) | null = null;
  private resolveEval: ((result: PositionEval) => void) | null = null;
  private rejectEval: ((reason?: unknown) => void) | null = null;
  private onEvaluationProgress: ((progress: StockfishSearchProgress) => void) | null = null;
  private targetDepth = 0;
  private _isReady = false;
  private currentMultiPv = 1;
  private showWdl = false;
  private limitedStrengthElo: number | null = null;
  private destroyed = false;

  get isReady(): boolean {
    return this._isReady;
  }

  get engineBuild(): string | null {
    return this.engineUrl?.split("/").at(-1) ?? null;
  }

  /**
   * Starts the Stockfish engine with the given preset configuration.
   * Returns a promise that resolves when the engine is ready for commands.
   */
  async init(preset: AnalysisPreset, onDownloadProgress?: (percent: number) => void): Promise<void> {
    const native = desktopStockfish();
    if (native) {
      try {
        await this.initPreset(preset, onDownloadProgress, native);
        return;
      } catch (error) {
        if (this.destroyed) throw error;
        // A missing or crashing native binary must not block analysis; the browser engine remains available.
        this.resetFailedWorker();
      }
    }
    try {
      await this.initPreset(preset, onDownloadProgress);
    } catch (error) {
      if (this.destroyed || preset.useFullEngine || preset.forceSingleThread || !supportsThreadedAnalysis())
        throw error;

      this.resetFailedWorker();
      await this.initPreset({ ...preset, threads: 1, forceSingleThread: true }, onDownloadProgress);
    }
  }

  private async initPreset(
    preset: AnalysisPreset,
    onDownloadProgress?: (percent: number) => void,
    native?: DesktopStockfish,
  ): Promise<void> {
    this.destroyed = false;
    if (native) this.engineUrl = native.build;
    const worker = native ? createNativeTransport(native) : await this.loadBrowserWorker(preset, onDownloadProgress);
    this.worker = worker;

    // Keep one timeout across UCI startup and option allocation.
    let startupTimeout: ReturnType<typeof setTimeout>;
    await new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
      startupTimeout = setTimeout(
        () =>
          this.rejectPending(
            worker,
            new Error(
              "The analysis engine timed out during initialization. Your browser may not support multi-threaded analysis.",
            ),
          ),
        15000,
      );

      worker.onerror = (e) => {
        clearTimeout(startupTimeout);
        e.preventDefault();
        const detail = e.message?.trim();
        this.rejectPending(
          worker,
          new Error(
            detail ? `The analysis engine failed to load: ${detail}` : "The analysis engine stopped unexpectedly.",
          ),
        );
      };

      worker.onmessageerror = () => {
        clearTimeout(startupTimeout);
        this.rejectPending(worker, new Error("The analysis engine returned an unreadable response."));
      };

      worker.onmessage = (e: MessageEvent<unknown>) => {
        const line = typeof e.data === "string" ? e.data : "";
        this.handleMessage(line);
      };

      this.send("uci");
    });

    // Configure engine options — always start at MultiPV 1 (cheapest, strongest
    // single-line search per Stockfish's own docs: MultiPV>1 weakens best-move
    // quality and costs extra search time). Full preset.multiPv is only applied
    // on-demand via setMultiPv() when re-evaluating a flagged mistake for
    // alternative lines — see game-analyzer.ts.
    const { threads, hash } = native
      ? nativeSearchOptions(preset, native)
      : { threads: resolveThreads(preset.threads), hash: preset.hash };
    this.send(`setoption name Threads value ${threads}`);
    this.send(`setoption name Hash value ${hash}`);
    this.send(`setoption name MultiPV value 1`);
    this.currentMultiPv = 1;
    // WDL output is not consumed by the analyzer. Disabling it shortens every
    // high-frequency UCI info message crossing onto the browser main thread.
    this.send(`setoption name UCI_ShowWDL value false`);
    this.showWdl = false;
    this.send("isready");

    await new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });

    clearTimeout(startupTimeout!);
    this._isReady = true;
  }

  private async loadBrowserWorker(
    preset: AnalysisPreset,
    onDownloadProgress?: (percent: number) => void,
  ): Promise<Worker> {
    if (preset.useFullEngine && !supportsThreadedAnalysis()) {
      throw new Error(
        "Deep analysis requires browser shared-memory support. Choose Quick or Regular analysis on this device.",
      );
    }
    const url = getEngineUrl(preset);
    this.engineUrl = url;

    // Pre-fetch WASM to track download progress (7-108MB)
    const wasmUrl = url.replace(".js", ".wasm");
    onDownloadProgress?.(0);
    try {
      const response = await fetch(wasmUrl);
      const contentLength = response.headers.get("content-length");
      const total = contentLength ? Number.parseInt(contentLength, 10) : 0;
      let received = 0;
      if (total > 0 && response.body) {
        const reader = response.body.getReader();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (this.destroyed) {
            await reader.cancel();
            throw createCancellationError();
          }
          received += value.length;
          onDownloadProgress?.(Math.min(99, Math.round((received / total) * 100)));
        }
      }
      if (preset.useFullEngine) {
        const measuredBytes = received || total;
        if (measuredBytes > 0 && measuredBytes < MIN_FULL_ENGINE_WASM_BYTES) {
          throw new FullEngineAssetMismatchError(measuredBytes);
        }
      }
    } catch (error) {
      if (error instanceof FullEngineAssetMismatchError) throw error;
      // Pre-fetch failed — Worker will fetch it anyway (just no progress bar)
    }
    if (this.destroyed) throw createCancellationError();
    onDownloadProgress?.(100);
    return new Worker(url);
  }

  private resetFailedWorker(): void {
    this.worker?.terminate();
    this.worker = null;
    this._isReady = false;
    this.resolveReady = null;
    this.rejectReady = null;
    this.resolveEval = null;
    this.rejectEval = null;
    this.onEvaluationProgress = null;
    this.limitedStrengthElo = null;
  }

  /**
   * Switches the engine's MultiPV setting. Only call this between searches
   * (never mid-`go`) — requires an isready round-trip so it's not free,
   * but it's cheap relative to the search itself.
   */
  private async setMultiPv(multiPv: number): Promise<void> {
    if (this.currentMultiPv === multiPv) return;
    this.send(`setoption name MultiPV value ${multiPv}`);
    this.currentMultiPv = multiPv;
    this.send("isready");
    await new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
  }

  private async setShowWdl(includeWdl: boolean): Promise<void> {
    if (this.showWdl === includeWdl) return;
    this.send(`setoption name UCI_ShowWDL value ${includeWdl ? "true" : "false"}`);
    this.showWdl = includeWdl;
    this.send("isready");
    await new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
  }

  private async setLimitedStrength(rating: number | null): Promise<void> {
    if (!this.worker || !this._isReady) throw new Error("Engine not initialized");
    const elo = rating == null ? null : clampStockfishElo(rating);
    if (this.limitedStrengthElo === elo) return;
    if (elo == null) {
      this.send("setoption name UCI_LimitStrength value false");
    } else {
      this.send("setoption name UCI_LimitStrength value true");
      this.send(`setoption name UCI_Elo value ${elo}`);
    }
    this.limitedStrengthElo = elo;
    this.send("isready");
    await new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
  }

  /**
   * Evaluates a position to a given depth.
   * If multiPv is provided and differs from the current setting, switches
   * MultiPV first (costs an isready round-trip, so only pass this for
   * positions that actually need alternative lines — mistakes/blunders/
   * imprecisions, not the cheap per-move baseline pass).
   */
  async evaluate(
    fen: string,
    depth: number,
    multiPv?: number,
    onProgress?: (progress: StockfishSearchProgress) => void,
    includeWdl = false,
  ): Promise<PositionEval> {
    if (!this.worker || !this._isReady) {
      return Promise.reject(new Error("Engine not initialized"));
    }

    if (multiPv != null) {
      await this.setMultiPv(multiPv);
    }
    if (this.showWdl !== includeWdl) await this.setShowWdl(includeWdl);

    this.latestInfoByPv.clear();
    this.onEvaluationProgress = onProgress ?? null;
    this.targetDepth = depth;
    this.send(`position fen ${fen}`);
    this.send(`go depth ${depth}`);

    return new Promise<PositionEval>((resolve, reject) => {
      this.resolveEval = resolve;
      this.rejectEval = reject;
    });
  }

  async evaluateAtElo(fen: string, depth: number, rating: number, signal?: AbortSignal): Promise<PositionEval> {
    await this.setLimitedStrength(rating);
    try {
      signal?.throwIfAborted();
      return await this.evaluate(fen, depth);
    } finally {
      await this.setLimitedStrength(null);
    }
  }

  /** Removes search-order history before a reproducible evidence search. */
  async clearHash(): Promise<void> {
    if (!this.worker || !this._isReady) throw new Error("Engine not initialized");
    const ready = new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
    this.send("ucinewgame");
    this.send("setoption name Clear Hash");
    this.send("isready");
    await ready;
  }

  /** Stops any ongoing search */
  stop(): void {
    if (this.worker) this.send("stop");
  }

  /** Terminates the worker */
  destroy(): void {
    this.destroyed = true;
    const cancellation = createCancellationError();
    this.rejectReady?.(cancellation);
    this.rejectReady = null;
    this.resolveReady = null;
    this.rejectEval?.(cancellation);
    this.rejectEval = null;
    this.resolveEval = null;
    this.onEvaluationProgress = null;
    this.limitedStrengthElo = null;
    if (this.worker) {
      this.send("quit");
      this.worker.terminate();
      this.worker = null;
      this.engineUrl = null;
      this._isReady = false;
    }
  }

  private send(cmd: string): void {
    this.worker?.postMessage(cmd);
  }

  private rejectPending(worker: EngineTransport, reason: Error): void {
    if (this.worker !== worker || this.destroyed) return;
    this._isReady = false;
    const rejectReady = this.rejectReady;
    const rejectEval = this.rejectEval;
    this.rejectReady = null;
    this.resolveReady = null;
    this.rejectEval = null;
    this.resolveEval = null;
    this.onEvaluationProgress = null;
    rejectReady?.(reason);
    rejectEval?.(reason);
  }

  private handleMessage(line: string): void {
    // Handle "uciok" — engine is UCI-ready
    if (line === "uciok" && this.resolveReady) {
      this.resolveReady();
      this.resolveReady = null;
      this.rejectReady = null;
      return;
    }

    // Handle "readyok" — engine is configured and ready
    if (line === "readyok" && this.resolveReady) {
      this.resolveReady();
      this.resolveReady = null;
      this.rejectReady = null;
      return;
    }

    // Collect info lines during search
    if (line.startsWith("info ") && line.includes(" pv ")) {
      // Stockfish emits info lines in increasing search depth. Keep only the
      // latest complete PV for each rank; avoid several regex scans on every
      // high-frequency worker message. Full parsing happens once at bestmove.
      const rank = Number(line.match(/\bmultipv (\d+)/)?.[1] ?? 1);
      if (!/\b(?:lowerbound|upperbound)\b/.test(line)) this.latestInfoByPv.set(rank, line);
      if (rank === 1 && this.onEvaluationProgress) {
        const depth = Number(line.match(/\bdepth (\d+)/)?.[1]);
        const nodes = Number(line.match(/\bnodes (\d+)/)?.[1]);
        if (Number.isFinite(depth))
          this.onEvaluationProgress({
            depth,
            nodes: Number.isFinite(nodes) ? nodes : 0,
            targetDepth: this.targetDepth,
            ...(!/\b(?:lowerbound|upperbound)\b/.test(line) && /\bscore (?:cp|mate) -?\d+/.test(line)
              ? { evaluation: this.parseSearchResult(`bestmove ${line.split(" pv ")[1]?.split(" ")[0] ?? ""}`) }
              : {}),
          });
      }
    }

    // Handle "bestmove" — search is complete
    if (line.startsWith("bestmove ") && this.resolveEval) {
      const result = this.parseSearchResult(line);
      this.resolveEval(result);
      this.resolveEval = null;
      this.rejectEval = null;
      this.onEvaluationProgress = null;
    }
  }

  private parseSearchResult(bestMoveLine: string): PositionEval {
    const bestMove = bestMoveLine.split(" ")[1] ?? "";

    // Parse all info lines to get the deepest complete evaluation
    const pvMap = new Map<number, PvLine>();
    let maxDepth = 0;
    let nodes = 0;
    let bestCp = 0;
    let bestMate: number | null = null;
    let bestPv: string[] = [];

    for (const line of this.latestInfoByPv.values()) {
      const depthMatch = line.match(/\bdepth (\d+)/);
      const pvRankMatch = line.match(/\bmultipv (\d+)/);
      const cpMatch = line.match(/\bscore cp (-?\d+)/);
      const mateMatch = line.match(/\bscore mate (-?\d+)/);
      const pvMatch = line.match(/\bpv (.+)/);
      const nodesMatch = line.match(/\bnodes (\d+)/);
      const wdlMatch = line.match(/\bwdl (\d+) (\d+) (\d+)/);

      if (!depthMatch) continue;
      const depth = Number.parseInt(depthMatch[1], 10);
      const rank = pvRankMatch ? Number.parseInt(pvRankMatch[1], 10) : 1;
      const cp = cpMatch
        ? Number.parseInt(cpMatch[1], 10)
        : mateMatch
          ? Number.parseInt(mateMatch[1], 10) > 0
            ? 10000
            : -10000
          : 0;
      const mate = mateMatch ? Number.parseInt(mateMatch[1], 10) : null;
      const pv = pvMatch ? pvMatch[1].trim().split(/\s+/) : [];
      if (nodesMatch) nodes = Number.parseInt(nodesMatch[1], 10);

      if (depth >= maxDepth) {
        maxDepth = depth;
        if (rank === 1) {
          bestCp = cp;
          bestMate = mate;
          bestPv = pv;
        }
        pvMap.set(rank, {
          rank,
          depth,
          cp,
          mate,
          moveUci: pv[0] ?? "",
          continuation: pv.slice(1),
          ...(wdlMatch
            ? {
                wdl: {
                  win: Number.parseInt(wdlMatch[1], 10),
                  draw: Number.parseInt(wdlMatch[2], 10),
                  loss: Number.parseInt(wdlMatch[3], 10),
                },
              }
            : {}),
        });
      }
    }

    const pvLines = Array.from(pvMap.values()).sort((a, b) => a.rank - b.rank);

    return {
      cp: bestCp,
      mate: bestMate,
      bestMove,
      pv: bestPv,
      depth: maxDepth,
      nodes,
      pvLines,
    };
  }
}

const createCancellationError = (): Error => {
  const error = new Error("Analysis cancelled");
  error.name = "AbortError";
  return error;
};
